// @flow

import * as React from 'react';

import { CornerDownRight } from 'lucide-react';
import { useDispatch } from 'react-redux';

import {
  selectOneProductErrors,
  getCart,
  type ProductError as CartProductError,
  removeProducts,
  updateCartProduct,
  useDiscountDetails,
  type DecoratedProduct,
} from '@gandi/cart';
import { notifyDanger } from '@gandi/notifications';
import { mailboxes } from '@gandi/redux-resource-price';
import { useTranslator } from '@gandi/translate';
import { Base, Fqdn } from '@gandi/ui';

import { fetchMailboxesPrices } from 'src/MailboxCreate/modules/actions';

import { useNotifications, useAppSelector } from 'src/hooks';

import { ListRow } from '../../ListRow';

import ProductErrorItem from './components/ProductErrorItem';
import ProductItemBase from './components/ProductItemBase';
import {
  DiscountRow,
  PlanToggleGroupRow,
  TitleRow,
  TotalRow,
} from './components/Rows';
import { usePlanOptions } from './modules/hooks';

type Props = {
  product: DecoratedProduct,
};

export default function Mailboxv2Renew({ product }: Props): React.MixedElement {
  const dispatch = useDispatch();
  const { __, formatDate } = useTranslator();

  const { notifyMaybeApiError } = useNotifications();

  const updatePlanHandler = async (evt: SyntheticInputEvent<>) => {
    evt.preventDefault();

    // the value is always a string
    // here we are passing a duration
    // it has to be a number kind
    const duration = parseInt(evt.target.value, 10);

    try {
      await dispatch(
        updateCartProduct({
          productId: product.id,
          payload: { ...product.payload, duration },
        }),
      );

      await dispatch(getCart());
    } catch (e) {
      notifyMaybeApiError(e);
    }
  };

  const discountDetails = useDiscountDetails({ product });

  const isFetchingPrices = useAppSelector(state =>
    mailboxes.priceMailboxesResource.selectIsFetchingOne(
      state,
      'mailboxv2_create',
    ),
  );
  const isLoadedPrices = useAppSelector(state =>
    mailboxes.priceMailboxesResource.selectIsLoadedOne(
      state,
      'mailboxv2_create',
    ),
  );

  const productErrors: Array<CartProductError> = useAppSelector(state =>
    selectOneProductErrors(state, {
      productId: product.id,
    }),
  );

  React.useEffect(() => {
    if (!isFetchingPrices && !isLoadedPrices) {
      dispatch(fetchMailboxesPrices());
    }
  }, [dispatch, isFetchingPrices, isLoadedPrices]);

  /**
   * Remove all mailboxes of a defined productType of the cart.
   */
  const removeFromCart = (e: any) => {
    e.preventDefault();

    if (product.quantity) {
      dispatch(removeProducts(product.ids.map(id => ({ id }))))
        .then(() => dispatch(getCart()))
        .catch(() => {
          notifyDanger({
            message: __('An error has occurred. Please try again.'),
          });
        });
    }
  };

  const planOptions = usePlanOptions(product.payload);

  // attempt to show the email
  const email = product.payload.email || product.payload.fqdn || '';

  const label = __('Mailbox');

  const capacity = product.payload.capacity;
  const expirationDate = product.expirationDate;

  const isConvert = product.payload.fake_renew;

  const info =
    expirationDate &&
    __('Expires on %(dateEnd)s', {
      dateEnd: formatDate(expirationDate, 'LL'),
    });

  return (
    <ProductItemBase
      header={
        <>
          <TitleRow
            label={label}
            productType={product.productType}
            handleRemoveAction={removeFromCart}
            // display default remove step if it is a conversion
            warningContent={
              isConvert ? undefined : (
                <Base tagName="p" margin="none">
                  {__(
                    'Warning: Not renewing the mailbox will result in the permanent and total loss of all the mailbox data after the expiration date.',
                  )}
                </Base>
              )
            }
          />
          <ProductErrorItem errors={productErrors} productId={product.id} />
          <PlanToggleGroupRow
            options={planOptions}
            value={product.payload.duration || 1}
            onChange={updatePlanHandler}
            productId={product.id}
          />
          <ListRow>
            {__('Mailbox %(name)s %(capacity)s %(plan)s', {
              // XXX: what if capacity is undefined or may be use and fix MailboxProduct instead?
              capacity: capacity || 0,
              name: product.payload.name || '',
              plan:
                product.payload.duration === 12
                  ? __('yearly subscription')
                  : __('monthly subscription'),
            })}
          </ListRow>
        </>
      }
      footer={
        <TotalRow
          hasTaxes={false}
          totalPrice={
            product.computedTotalPrices?.beforeTaxes ??
            product.total?.before_taxes
          }
          priceCrossedOut={product.total?.before_discount}
          hasDiscount={!!discountDetails?.length}
        />
      }
    >
      <ListRow
        intent="muted"
        icon={<CornerDownRight size={16} />}
        title={<Fqdn color="base">{email}</Fqdn>}
        options={info ? [info] : undefined}
        optionsArrow={false}
      />

      {discountDetails?.map(discount => (
        <DiscountRow
          key={`discount-${product.id}-${discount.label}`}
          label={discount.label}
          price={discount.price}
        />
      ))}
    </ProductItemBase>
  );
}
